import {
    useController,
    type ControllerRenderProps,
    type FieldPath,
    type FieldValues,
    type UseControllerProps,
} from 'react-hook-form'

import { LabeledInput, LabeledInputProps } from './LabeledInput'

type UseControllerPropsPickedKeys =
    | 'name'
    | 'control'
    | 'defaultValue'
    | 'disabled'

export type RhfLabeledInputProps<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
> = Omit<
    // input props
    LabeledInputProps,
    // omit controller input
    | UseControllerPropsPickedKeys
    // omit controller output
    | keyof ControllerRenderProps<TFieldValues, TName>
> &
    // pick controller
    Pick<
        UseControllerProps<TFieldValues, TName, TTransformedValues>,
        UseControllerPropsPickedKeys
    >

const extractControllerProps = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
>({
    name,
    control,
    defaultValue,
    disabled,
    ...rest
}: RhfLabeledInputProps<TFieldValues, TName, TTransformedValues>): [
    Pick<
        RhfLabeledInputProps<TFieldValues, TName, TTransformedValues>,
        UseControllerPropsPickedKeys
    >,
    Omit<
        RhfLabeledInputProps<TFieldValues, TName, TTransformedValues>,
        UseControllerPropsPickedKeys
    >,
] => [
    {
        name,
        control,
        defaultValue,
        disabled,
    },
    rest,
]

export const RhfLabeledInput = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
    TTransformedValues = TFieldValues,
>(
    props: RhfLabeledInputProps<TFieldValues, TName, TTransformedValues>
) => {
    const [controllerProps, { error, ...restProps }] =
        extractControllerProps(props)
    const { field, fieldState } = useController(controllerProps)

    /*
    field
   name, ref, disabled
   onChange, onBlur, value
   */

    return (
        <LabeledInput
            {...restProps}
            {...controllerProps}
            {...field}
            error={fieldState.error?.message}
        />
    )
}
